import React, { useState, useEffect } from 'react';
import {
  useGetForgotPasswordUserQuery,
  useUpdateUserPasswordMutation
} from '../../../features/authentication/authApiSlice';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Button, Form, ListGroup, DropdownButton, Dropdown, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { useLoginContext } from '../context';

function SetNewPassword() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('resetToken');
  const isMobile = searchParams.get('mobile');
  const { data, isFetching, isSuccess, isError } = useGetForgotPasswordUserQuery(resetToken);
  const [updatePassword, { isLoading }] = useUpdateUserPasswordMutation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState();
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordLengthValid, setPasswordLengthValid] = useState(false);
  const [passwordCharactersValid, setPasswordCharactersValid] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const loginContext = useLoginContext();

  useEffect(() => {
    if (isFetching) return;
    if (isSuccess) {
      // If there's only 1 account select it
      setSelectedUserId(data && data.length > 1 ? null : 0);
    }
  }, [isSuccess]);
  
  useEffect(() => {
    if(isError){
      loginContext.setErrorMessage('Reset password token has expired');
      loginContext.setShowError(true);
      navigate('/login');
    }
  },[isError]);

  useEffect(() => {
    const userAgent = navigator.userAgent;

    if ((/Mobile/i.test(userAgent) || /Tablet/i.test(userAgent)) && isMobile == 'true') {
      window.location.assign(`castleCompanion://setNewPassword?resetToken=${resetToken}`);
    }
  },[]);

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setPasswordValid(
      e.target.value == data.at(selectedUserId).loginId || e.target.value.length == 0 ? false : true
    );
    setPasswordLengthValid(e.target.value.length < 4 ? false : true);
    setPasswordCharactersValid(
      e.target.value.match('[",\'</]') || e.target.value.length == 0 ? false : true
    );
    setPasswordsMatch(
      confirmPassword && confirmPassword.length > 0 && confirmPassword != e.target.value
        ? false
        : true
    );
  };

  const handleConfirmPasswordBlur = (e) => {
    setPasswordsMatch(
      newPassword && newPassword.length > 0 && newPassword != e.target.value ? false : true
    );
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (selectedUserId == null || selectedUserId == undefined) {
      setError('Please select an account');
      return;
    }

    if (!passwordValid || !passwordLengthValid || !passwordCharactersValid || !passwordsMatch)
      return;

    try {
      await updatePassword({
        resetToken: resetToken,
        userId: data.at(selectedUserId).userId,
        newPassword: newPassword
      }).unwrap();
      setNewPassword('');
      setConfirmPassword('');
      setShowModal(true);
    } catch (ex) {
      if (!ex?.status || (ex && ex.status === 'FETCH_ERROR')) {
        setError('No server response');
      } else if (ex.status == 400) {
        setError('Reset password token has expired');
      }
    }
  }

  return (
    <>
      <ListGroup className="login-card-body" variant="flush">
        <ListGroup.Item>
          <div
            className="mx-auto text-color"
            style={{
              width: '90%',
              textAlign: 'center',
              fontWeight: '500',
              fontSize: '18px'
            }}>
            Reset Password
          </div>
        </ListGroup.Item>
        <Form onSubmit={handleSubmit}>
          {data && data.length > 1 ? (
            <>
              <ListGroup.Item>
                <p>
                  Multiple accounts were found matching this email. Please select which account you
                  want to reset the password for in the dropdown below.
                </p>
              </ListGroup.Item>
              <ListGroup.Item className="text-center d-flex flex-row align-items-center justify-content-center ">
                <DropdownButton
                  onSelect={(e) => setSelectedUserId(e)}
                  title={
                    selectedUserId
                      ? `${data.at(selectedUserId).district} - ${data.at(selectedUserId).lastName
                      }, ${data.at(selectedUserId).firstName} (${data.at(selectedUserId).accountType
                      })`
                      : 'Select Account'
                  }
                  id="newPasswordAccount"
                  style={{ width: '100%' }}
                  variant="castleDropdown"
                  drop="down-centered"
                  required>
                  {data.map((user, index) => {
                    return (
                      <Dropdown.Item key={`user${index}`} value={user.studentId} eventKey={index}>
                        {user.district} - {user.lastName}, {user.firstName} ({user.accountType})
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </ListGroup.Item>
            </>
          ) : data ? (
            <ListGroup.Item className="text-center">
              <span className="me-2">LoginID:</span>
              <span>{`${data && data.at(0).emailAddress}`}</span>
            </ListGroup.Item>
          ) : (
            <ListGroup.Item className="text-center">Loading...</ListGroup.Item>
          )}
          <ListGroup.Item>
            <Form.Group id="newPassword">
              <Form.Label>Enter New Password</Form.Label>
              <div>
                <Form.Control
                  disable={isFetching || isLoading}
                  className={passwordFocused ? 'loginTextBox NewPasswordFocus' : 'loginTextBox'}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  type={showConfirmPassword ? 'text' : 'password'}
                  onChange={handleNewPasswordChange}
                  value={newPassword}
                  required
                />
                <FontAwesomeIcon
                  className="loginTextboxIcon"
                  style={{ top: passwordFocused ? '20%' : '' }}
                  icon={faLock}
                />
              </div>
              <div
                className={
                  passwordFocused
                    ? 'newPasswordRequirements showRequirements'
                    : 'newPasswordRequirements'
                }>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordValid ? faCircleCheck : faCircleXmark}
                    color={passwordValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>Cannot be the same as your Login ID</span>
                </div>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordLengthValid ? faCircleCheck : faCircleXmark}
                    color={passwordLengthValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>Must be at least 4 characters in length</span>
                </div>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordCharactersValid ? faCircleCheck : faCircleXmark}
                    color={passwordCharactersValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>{'Cannot include the following special characters (",\'</)'}</span>
                </div>
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item>
            <Form.Group id="confirmPassword">
              <Form.Label>Confirm New Password</Form.Label>
              <div>
                <Form.Control
                  disable={isFetching || isLoading}
                  className="loginTextBox"
                  type={showConfirmPassword ? 'text' : 'password'}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={handleConfirmPasswordBlur}
                  value={confirmPassword}
                  required
                />
                <FontAwesomeIcon
                  className="loginTextboxIcon"
                  style={{ cursor: 'pointer' }}
                  icon={faEye}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>
            </Form.Group>
          </ListGroup.Item>
          {!passwordsMatch && (
            <ListGroup.Item>
              <span style={{ color: '#F84646' }}>Passwords do not match</span>
            </ListGroup.Item>
          )}

          <ListGroup.Item>
            <Button
              disable={isFetching || isLoading}
              variant="castleRed"
              className="submitButton"
              type="submit">
              Submit
            </Button>
          </ListGroup.Item>
        </Form>

        {error && (
          <ListGroup.Item>
            <div className="d-flex flex-column align-content-center">
              <div className='mx-auto error'>
                {error}
              </div>
              <div className='mx-auto text-color'>
                Please try again or contact customer support at 800-345-7607
              </div>
            </div>
          </ListGroup.Item>
        )}

        <ListGroup.Item>
          <Button
            disable={isFetching || isLoading}
            variant="castleWhite"
            className="backButton"
            onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </ListGroup.Item>
      </ListGroup>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Password updated</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Your password has been updated. You can return to the login page and login with your new
            password
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="castleRed" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SetNewPassword;
